/**
 * FIELD REPORT PAGE - Mobile-First HUD (High-Density)
 * 
 * Beta 3 Implementation - Periodic Update Model
 * 
 * Shows coaches how their roster is performing after admin triggers periodic updates.
 * This is NOT a live streaming feed - data updates only when admin triggers updates.
 * 
 * Route: /pools/:poolId/field-report
 * Data Source: GET /api/v1/pools/{poolId}/entry (existing endpoint)
 * 
 * HIGH-DENSITY MOBILE DESIGN:
 * - Compact row layout (5-6 players visible at once)
 * - No bloated card padding
 * - Progress bars inline, not boxed
 * - Touch targets ≥ 44px on interactive elements only
 * 
 * VOCABULARY COMPLIANCE:
 * - Above/Below (coaching language per architect)
 * - Impact (not units)
 * - Target Hit / Target Missed (not won/lost)
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1';

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

type AllocationStatus = 'IN_PLAY' | 'UPDATED' | 'FINAL';

interface FieldReportRowVM {
  slot: string;
  slotIndex: number;
  playerName: string;
  team: string;
  opponent: string;
  impact: number;
  target: {
    stat: string;
    line: number;
    direction: 'ABOVE' | 'BELOW' | null;
    directionUnknown: boolean;
  };
  actualValue: number | null;
  resolutionStatus: string | null;
  payout: number | null;  // PHASE 1: Added for resolution display
  role: string | null;
  status: AllocationStatus;
  gameContext?: string | null;  // V9: Optional game clock/quarter context
}

interface GapResult {
  value: number;
  message: string;
  isPositive: boolean;
}

interface DirectionResult {
  direction: 'ABOVE' | 'BELOW' | null;
  directionUnknown: boolean;
}

interface StatusStyles {
  text: string;
  bar: string;
  border: string;
  label: string;
  bgAccent: string;
}

// =============================================================================
// SVG ICONS (Inline - Field Report only)
// =============================================================================

const ArrowLeftIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
  </svg>
);

const RefreshIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const CheckIcon = ({ className = "w-3 h-3" }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const XIcon = ({ className = "w-3 h-3" }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ClockIcon = ({ className = "w-3 h-3" }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Unwrap entry response - handles both wrapper and raw shapes
 */
const unwrapEntry = (response: unknown): any | null => {
  if (!response) return null;
  if (typeof response === 'object' && 'entry' in (response as object)) {
    return (response as { entry: any }).entry;
  }
  return response;
};

/**
 * Map direction from OVER/UNDER to ABOVE/BELOW with safe fallback
 */
const mapDirection = (side: string | null | undefined): DirectionResult => {
  if (!side) {
    return { direction: 'ABOVE', directionUnknown: true };
  }
  
  const upper = side.toUpperCase();
  
  if (upper === 'OVER') {
    return { direction: 'ABOVE', directionUnknown: false };
  }
  if (upper === 'UNDER') {
    return { direction: 'BELOW', directionUnknown: false };
  }
  
  return { direction: 'ABOVE', directionUnknown: true };
};

/**
 * Derive allocation status from actual value and resolution status
 */
const deriveStatus = (
  actualValue: number | null | undefined,
  resolutionStatus: string | null | undefined
): AllocationStatus => {
  if (resolutionStatus && ['HIT', 'MISS', 'PUSH'].includes(resolutionStatus.toUpperCase())) {
    return 'FINAL';
  }
  if (actualValue !== null && actualValue !== undefined) {
    return 'UPDATED';
  }
  return 'IN_PLAY';
};

/**
 * Calculate THE GAP - only when actualValue exists
 */
const calculateGap = (
  line: number,
  direction: 'ABOVE' | 'BELOW' | null,
  actualValue: number | null
): GapResult | null => {
  if (actualValue === null || actualValue === undefined || direction === null) {
    return null;
  }
  
  const diff = actualValue - line;
  
  if (direction === 'ABOVE') {
    if (diff >= 0) {
      return { value: diff, message: `+${Math.abs(diff).toFixed(1)}`, isPositive: true };
    }
    return { value: Math.abs(diff), message: `${diff.toFixed(1)}`, isPositive: false };
  } else {
    if (diff <= 0) {
      return { value: Math.abs(diff), message: `+${Math.abs(diff).toFixed(1)}`, isPositive: true };
    }
    return { value: diff, message: `-${diff.toFixed(1)}`, isPositive: false };
  }
};

/**
 * Get progress bar width (0-100%)
 */
const getProgressWidth = (line: number, actualValue: number | null): number => {
  if (actualValue === null || actualValue === undefined || line === 0) {
    return 0;
  }
  return Math.min(100, (actualValue / line) * 100);
};

/**
 * Format stat name for display (remove underscores, abbreviate)
 */
const formatStatName = (stat: string): string => {
  if (!stat) return 'Stat';
  
  // Common abbreviations
  const abbrevMap: Record<string, string> = {
    'RUSHING_YARDS': 'Rush Yds',
    'RECEIVING_YARDS': 'Rec Yds',
    'PASSING_YARDS': 'Pass Yds',
    'COMPLETIONS': 'Comp',
    'RECEPTIONS': 'Rec',
    'CARRIES': 'Carries',
    'TOUCHDOWNS': 'TD',
    'PASSING_TOUCHDOWNS': 'Pass TD',
    'RUSHING_TOUCHDOWNS': 'Rush TD',
    'RECEIVING_TOUCHDOWNS': 'Rec TD',
    'INTERCEPTIONS': 'INT',
    'ASSISTS': 'Ast',
    'REBOUNDS': 'Reb',
    'POINTS': 'Pts',
    'THREE_POINTERS': '3PT',
  };
  
  const upper = stat.toUpperCase();
  if (abbrevMap[upper]) return abbrevMap[upper];
  
  // Fallback: replace underscores and title case
  return stat.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
};

/**
 * Get status-based styling
 */
const getStatusStyles = (
  status: AllocationStatus, 
  resolutionStatus: string | null, 
  gap: GapResult | null
): StatusStyles => {
  // FINAL - resolved
  if (status === 'FINAL') {
    const isHit = resolutionStatus?.toUpperCase() === 'HIT';
    return { 
      text: isHit ? 'text-emerald-400' : 'text-red-400', 
      bar: isHit ? 'bg-emerald-500' : 'bg-red-500', 
      border: isHit ? 'border-emerald-500/30' : 'border-red-500/30', 
      label: isHit ? 'HIT' : 'MISS',
      bgAccent: isHit ? 'bg-emerald-500/10' : 'bg-red-500/10'
    };
  }
  // UPDATED - has live data, check if on track
  if (status === 'UPDATED' && gap) {
    if (gap.isPositive) {
      return { 
        text: 'text-emerald-400', 
        bar: 'bg-emerald-500', 
        border: 'border-emerald-500/30', 
        label: 'On Track',
        bgAccent: 'bg-emerald-500/10'
      };
    }
    return { 
      text: 'text-amber-400', 
      bar: 'bg-amber-500', 
      border: 'border-amber-500/30', 
      label: 'Chasing',
      bgAccent: 'bg-amber-500/10'
    };
  }
  // IN_PLAY - Pending/Upcoming
  return { 
    text: 'text-slate-400', 
    bar: 'bg-slate-600', 
    border: 'border-slate-800', 
    label: 'Upcoming',
    bgAccent: 'bg-slate-800/30'
  };
};

/**
 * Get role indicator color (thin left border)
 */
const getRoleBorderColor = (role: string | null): string => {
  if (!role) return 'bg-slate-700';
  const upperRole = role.toUpperCase().replace('_', ' ');
  if (upperRole === 'GAME BREAKER') return 'bg-amber-500';
  if (upperRole === 'PLAYMAKER') return 'bg-indigo-500';
  return 'bg-slate-700';
};

/**
 * Format relative time (e.g., "18s ago", "2m ago", "1h ago")
 * Apple HIG: Use natural language for time intervals
 */
const formatRelativeTime = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  
  if (diffSec < 60) {
    return `${diffSec}s ago`;
  }
  if (diffMin < 60) {
    return `${diffMin}m ago`;
  }
  if (diffHour < 24) {
    return `${diffHour}h ago`;
  }
  return `${Math.floor(diffHour / 24)}d ago`;
};

/**
 * Format absolute time (e.g., "Feb 2, 11:14 AM")
 * Apple HIG: Month Day, Time format for clarity
 */
const formatAbsoluteTime = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  }) + ', ' + date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Combined format: "18s ago (Feb 2, 11:14 AM)"
 */
const formatLastUpdateV9 = (date: Date | null): string => {
  if (!date) return '—';
  return `${formatRelativeTime(date)} (${formatAbsoluteTime(date)})`;
};

// =============================================================================
// ENTRY ADAPTER - Maps actual EntryData to FieldReportRowVM
// =============================================================================

const toFieldReportRows = (entry: any): FieldReportRowVM[] => {
  if (!entry || !entry.roster || !Array.isArray(entry.roster)) {
    return [];
  }
  
  return entry.roster.map((slot: any, index: number) => {
    const playerName = slot.player?.name || slot.playerName || slot.player_name || 'Unknown Player';
    const team = slot.player?.team || slot.team || '???';
    const opponent = slot.opponent || slot.opp || slot.player?.opp || '';
    const statType = slot.prop?.stat || slot.stat_type || slot.statType || 'Stat';
    const line = slot.prop?.line || slot.line_value || slot.line || 0;
    const sideValue = slot.side || slot.direction;
    const { direction, directionUnknown } = mapDirection(sideValue);
    const impact = slot.units || slot.allocation_units || slot.impact || 0;
    // Resolution data (Beta 3): nested under slot.resolution
    const actualValue =
      slot.resolution?.actualValue ??
      slot.resolution?.actual_value ??
      slot.actual_value ??
      slot.actualValue ??
      null;
    
    const resolutionStatus =
      slot.resolution?.status ??
      slot.resolution?.resolutionStatus ??
      slot.resolution_status ??
      slot.resolutionStatus ??
      null;
    const status = deriveStatus(actualValue, resolutionStatus);
    const role = slot.role || null;
    
    // PHASE 1: Extract payout from nested resolution
    const payout =
      slot.resolution?.payout ??
      slot.payout ??
      null;
    
    return {
      slot: slot.label || slot.slot_position || `Slot ${index + 1}`,
      slotIndex: slot.slot_index ?? slot.slot ?? index,
      playerName,
      team,
      opponent,
      impact,
      target: { stat: statType, line, direction, directionUnknown },
      actualValue,
      resolutionStatus,
      payout,  // PHASE 1: Include payout
      role,
      status,
      gameContext: null,  // V9: Placeholder - will render if API provides value
    };
  });
};

// =============================================================================
// COMPACT ROW COMPONENT (High-Density)
// =============================================================================

const AllocationRow = ({ row }: { row: FieldReportRowVM }) => {
  const gap = calculateGap(row.target.line, row.target.direction, row.actualValue);
  const styles = getStatusStyles(row.status, row.resolutionStatus, gap);
  const progress = getProgressWidth(row.target.line, row.actualValue);
  const hasLiveData = row.actualValue !== null;
  const showOpponent = row.opponent && row.opponent !== '' && row.opponent !== '???';
  const formattedStat = formatStatName(row.target.stat);
  const dirLabel = row.target.direction === 'ABOVE' ? 'Over' : row.target.direction === 'BELOW' ? 'Under' : '';
  
  const isFinal = row.status === 'FINAL';
  const isHit = row.resolutionStatus?.toUpperCase() === 'HIT';
  const isMiss = row.resolutionStatus?.toUpperCase() === 'MISS';
  
  return (
    <div className={`relative border-b border-slate-800/50 ${isMiss ? 'opacity-60' : ''}`}>
      {/* Left Role Indicator */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${getRoleBorderColor(row.role)}`} />
      
      <div className="pl-3 pr-2 py-2">
        {/* ROW 1: Slot + Name + Role Chip + Impact */}
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span className="text-[11px] font-black text-slate-500 bg-slate-900 px-1 py-0.5 rounded shrink-0">
              {row.slot}
            </span>
            <span className="text-[13px] font-bold text-white truncate">{row.playerName}</span>
            {/* Role Chip - Only render if role exists */}
            {row.role && (
              <span className={`text-[9px] font-black px-1.5 py-0.5 rounded shrink-0 ${
                row.role.toUpperCase().includes('GAME') || row.role.toUpperCase().includes('BREAKER')
                  ? 'text-amber-400 bg-amber-950/40 border border-amber-500/20'
                  : row.role.toUpperCase().includes('PLAY') || row.role.toUpperCase().includes('MAKER')
                  ? 'text-indigo-400 bg-indigo-950/40 border border-indigo-500/20'
                  : 'text-slate-400 bg-slate-800 border border-slate-700'
              }`}>
                {row.role.toUpperCase().includes('GAME') || row.role.toUpperCase().includes('BREAKER') ? '🔥' : 
                 row.role.toUpperCase().includes('PLAY') || row.role.toUpperCase().includes('MAKER') ? '⭐' : '🛡️'}
              </span>
            )}
          </div>
          <span className="text-[11px] font-bold text-indigo-400 shrink-0 ml-2">{row.impact}</span>
        </div>
        
        {/* ROW 2: Matchup + Target */}
        <div className="flex items-center justify-between text-[11px] mb-1.5">
          <span className="text-slate-500">
            {row.team}{showOpponent ? ` vs ${row.opponent}` : ''}
          </span>
          <span className="text-slate-400">
            {dirLabel} <span className="text-white font-mono">{row.target.line}</span> {formattedStat}
          </span>
        </div>
        
        {/* ROW 3: Progress Bar + Status (conditional) */}
        {hasLiveData ? (
          <div className="flex items-center gap-2">
            {/* Progress bar */}
            <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
              <div 
                className={`h-full ${styles.bar} transition-all duration-300`}
                style={{ width: `${progress}%` }}
              />
            </div>
            {/* Current / Target */}
            <span className="text-[11px] font-mono text-slate-500 shrink-0">
              <span className={styles.text}>{typeof row.actualValue === 'number' ? row.actualValue.toFixed(1) : row.actualValue}</span>
              <span className="text-slate-600">/{row.target.line}</span>
            </span>
            {/* Gap indicator */}
            {gap && (
              <span className={`text-[11px] font-bold ${styles.text} shrink-0`}>
                {gap.message}
              </span>
            )}
            {/* Final status icon */}
            {isFinal && (
              <span className={`shrink-0 ${isHit ? 'text-emerald-400' : 'text-red-400'}`}>
                {isHit ? <CheckIcon className="w-3.5 h-3.5" /> : <XIcon className="w-3.5 h-3.5" />}
              </span>
            )}
            {/* PHASE 1: Payout display for resolved allocations */}
            {isFinal && row.payout !== null && (
              <span className={`text-[11px] font-mono font-bold shrink-0 ml-1 ${row.payout >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {row.payout >= 0 ? '+' : ''}{row.payout}
              </span>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex-1 h-1.5 bg-slate-800/50 rounded-full" />
            <span className="text-[11px] text-slate-600 ml-2">{styles.label}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// EMPTY STATE
// =============================================================================

const EmptyState = ({ poolId }: { poolId: string }) => {
  const navigate = useNavigate();
  
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center min-h-screen bg-slate-950">
      <p className="text-slate-400 mb-4">No roster found for this pool.</p>
      <div className="space-y-3 w-full max-w-xs">
        <button
          onClick={() => navigate(`/pools/${poolId}/tunnel`)}
          className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-4 rounded-xl transition-colors min-h-[48px]"
        >
          Complete Roster
        </button>
        <button
          onClick={() => navigate(`/pools/${poolId}/leaderboard`)}
          className="w-full bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold py-4 rounded-xl transition-colors min-h-[48px]"
        >
          Back to Scoreboard
        </button>
      </div>
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const FieldReportPage: React.FC = () => {
  const { poolId } = useParams<{ poolId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = (user?.user_id ?? user?.id);
  const userIdStr = userId != null ? String(userId) : '';
  
  // Local state
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState<Date | null>(null);
  const [rawEntry, setRawEntry] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Inline fetch - matches War Room pattern
  const fetchEntry = useCallback(async () => {
    if (!poolId || !userIdStr) return null;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/pools/${poolId}/entry`, {
        headers: { 'X-User-ID': userIdStr },
      });

      if (response.status === 404) {
        setRawEntry(null);
        return null;
      }

      if (!response.ok) {
        const msg = `Failed to fetch entry: ${response.status}`;
        setError(msg);
        setRawEntry(null);
        return null;
      }

      const data = await response.json();
      setRawEntry(data);
      return data;
    } catch (err: any) {
      setError(err?.message || 'Failed to load entry');
      setRawEntry(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [poolId, userIdStr]);

  useEffect(() => {
    fetchEntry();
  }, [fetchEntry]);

  const refetch = fetchEntry;
  
  // Unwrap entry
  const entry = useMemo(() => unwrapEntry(rawEntry), [rawEntry]);
  
  // Transform to view model
  const rows = useMemo(() => toFieldReportRows(entry), [entry]);
  
  // Compute derived values
  const score = entry?.score ?? 0;
  const rank = entry?.rank ?? 0;
  const totalCoaches = entry?.total_entrants || entry?.totalCoaches || 0;
  
  const impactInPlay = useMemo(() => 
    rows.filter(r => r.status !== 'FINAL').reduce((sum, r) => sum + r.impact, 0),
    [rows]
  );
  
  const resolvedCount = useMemo(() => 
    rows.filter(r => r.status === 'FINAL').length,
    [rows]
  );
  
  // V9 Footer HUD computed values
  const hitsCount = useMemo(() => 
    rows.filter(r => r.resolutionStatus?.toUpperCase() === 'HIT').length,
    [rows]
  );
  
  const missesCount = useMemo(() => 
    rows.filter(r => r.resolutionStatus?.toUpperCase() === 'MISS').length,
    [rows]
  );
  
  const earnedPoints = useMemo(() => 
    rows.filter(r => r.status === 'FINAL').reduce((sum, r) => sum + (r.payout ?? 0), 0),
    [rows]
  );
  
  const onFieldCount = useMemo(() => 
    rows.filter(r => r.status === 'UPDATED').length,
    [rows]
  );
  
  const inLockerCount = useMemo(() => 
    rows.filter(r => r.status === 'IN_PLAY').length,
    [rows]
  );
  
  const impactOnField = useMemo(() => 
    rows.filter(r => r.status === 'UPDATED').reduce((sum, r) => sum + r.impact, 0),
    [rows]
  );
  
  const impactInLocker = useMemo(() => 
    rows.filter(r => r.status === 'IN_PLAY').reduce((sum, r) => sum + r.impact, 0),
    [rows]
  );
  
  const hasInPlayAllocations = useMemo(() => 
    rows.some(r => r.status === 'IN_PLAY' || r.status === 'UPDATED'),
    [rows]
  );
  
  // Sort roster: UPDATED → IN_PLAY → FINAL (HIT before MISS)
  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      const order = { 'UPDATED': 0, 'IN_PLAY': 1, 'FINAL': 2 };
      const aOrder = order[a.status] ?? 3;
      const bOrder = order[b.status] ?? 3;
      if (aOrder !== bOrder) return aOrder - bOrder;
      if (a.status === 'FINAL' && b.status === 'FINAL') {
        if (a.resolutionStatus?.toUpperCase() === 'HIT' && b.resolutionStatus?.toUpperCase() !== 'HIT') return -1;
        if (a.resolutionStatus?.toUpperCase() !== 'HIT' && b.resolutionStatus?.toUpperCase() === 'HIT') return 1;
      }
      return 0;
    });
  }, [rows]);
  
  // Manual refresh handler
  const handleRefresh = useCallback(async () => {
    if (!refetch) return;
    
    setIsRefreshing(true);
    try {
      const result = await refetch();
      if (result) {
        setLastFetchTime(new Date());
      }
    } catch (err) {
      console.error('[FieldReport] Refresh failed:', err);
    } finally {
      setIsRefreshing(false);
    }
  }, [refetch]);
  
  // Set initial lastFetchTime on successful load
  useEffect(() => {
    if (entry && !loading && !lastFetchTime) {
      setLastFetchTime(new Date());
    }
  }, [entry, loading, lastFetchTime]);
  
  // Optional polling - runs every 30s ONLY while hasInPlayAllocations
  useEffect(() => {
    if (!hasInPlayAllocations || !refetch) return;
    
    const pollInterval = setInterval(async () => {
      try {
        const result = await refetch();
        if (result) {
          setLastFetchTime(new Date());
        }
      } catch (err) {
        console.error('[FieldReport] Poll failed:', err);
      }
    }, 30000);
    
    return () => clearInterval(pollInterval);
  }, [hasInPlayAllocations, refetch]);
  
  // Navigation handlers
  const handleBack = () => {
    navigate(`/pools/${poolId}/leaderboard`);
  };
  
  // Loading state
  if (loading && !entry) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">
        <div className="text-center">
          <RefreshIcon className="w-8 h-8 animate-spin text-indigo-400 mx-auto mb-2" />
          <p className="text-slate-400">Loading Field Report...</p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error && !entry) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">
        <div className="text-center px-4">
          <p className="text-red-400 mb-4">Error loading entry: {error}</p>
          <button
            onClick={handleBack}
            className="bg-slate-800 hover:bg-slate-700 text-white font-bold py-3 px-6 rounded-xl transition-colors min-h-[48px]"
          >
            Back to Scoreboard
          </button>
        </div>
      </div>
    );
  }
  
  // Empty/404 state
  if (!entry || !entry.roster || rows.length === 0) {
    return <EmptyState poolId={poolId || ''} />;
  }
  
  return (
    <div className="min-h-screen bg-slate-950 text-white font-sans pb-52">
      
      {/* STICKY HEADER - Compact */}
      <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-md border-b border-slate-800">
        
        {/* Top Bar: Back + Title + Refresh */}
        <div className="flex items-center justify-between px-2 py-1.5">
          <button 
            onClick={handleBack}
            className="flex items-center text-slate-400 hover:text-white transition-colors min-h-[44px] min-w-[44px] px-2"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          
          <div className="text-center">
            <div className="flex items-center justify-center gap-2">
              {hasInPlayAllocations && (
                <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
              )}
              <span className="text-[14px] font-black text-white uppercase tracking-wide">
                Field Report
              </span>
            </div>
            <p className="text-[12px] text-slate-400">Your Game Plan</p>
          </div>
          
          <button 
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-400 hover:text-white disabled:opacity-50"
          >
            <RefreshIcon className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Scoreboard - Compact 3-Column */}
        <div className="grid grid-cols-3 border-t border-slate-800">
          <div className="py-1.5 px-2 text-center border-r border-slate-800">
            <p className="text-[11px] font-bold text-slate-500 uppercase">Score</p>
            <p className={`text-lg font-mono font-black ${score >= 0 ? 'text-emerald-400' : 'text-red-500'}`}>
              {score >= 0 ? '+' : ''}{score}
            </p>
          </div>
          <div className="py-1.5 px-2 text-center border-r border-slate-800">
            <p className="text-[11px] font-bold text-slate-500 uppercase">Rank</p>
            <p className="text-lg font-mono font-black text-white">
              #{rank || '—'}
              {totalCoaches > 0 && <span className="text-[11px] text-slate-600">/{totalCoaches}</span>}
            </p>
          </div>
          <div className="py-1.5 px-2 text-center">
            <p className="text-[11px] font-bold text-indigo-400 uppercase">In Play</p>
            <p className="text-lg font-mono font-black text-white">{impactInPlay}</p>
          </div>
        </div>
        
        {/* Last Updated - ALWAYS VISIBLE (V9 Requirement) */}
        <div className="bg-slate-950 px-3 py-1.5 flex items-center justify-center gap-1.5 border-t border-slate-800/50">
          <ClockIcon className="w-3 h-3 text-slate-500" />
          <span className="text-[11px] text-slate-400">
            Last updated: {formatLastUpdateV9(lastFetchTime)}
          </span>
          <span className="text-slate-600 mx-1">•</span>
          <span className="text-[11px] text-slate-500">
            {resolvedCount}/{rows.length} resolved
          </span>
        </div>
      </header>

      {/* ROSTER LIST - V9 Section Grouping */}
      <main className="bg-slate-900 pt-2">
        
        {/* ⚡ ON THE FIELD - Players with live data (UPDATED status) */}
        {(() => {
          const onFieldRows = sortedRows.filter(r => r.status === 'UPDATED');
          return onFieldRows.length > 0 && (
            <section className="mb-4">
              <div className="flex items-center gap-2 mb-2 px-4">
                <span className="text-base">⚡</span>
                <h2 className="text-[12px] font-black text-emerald-400 uppercase tracking-wider">
                  On the Field ({onFieldRows.length})
                </h2>
              </div>
              {onFieldRows.map((row, index) => (
                <AllocationRow key={`field-${row.slot}-${index}`} row={row} />
              ))}
            </section>
          );
        })()}
        
        {/* 🕐 IN LOCKER ROOM - Awaiting game start (IN_PLAY status) */}
        {(() => {
          const lockerRows = sortedRows.filter(r => r.status === 'IN_PLAY');
          return lockerRows.length > 0 && (
            <section className="mb-4">
              <div className="flex items-center gap-2 mb-2 px-4">
                <span className="text-base">🕐</span>
                <h2 className="text-[12px] font-black text-slate-400 uppercase tracking-wider">
                  In Locker Room ({lockerRows.length})
                </h2>
              </div>
              {lockerRows.map((row, index) => (
                <AllocationRow key={`locker-${row.slot}-${index}`} row={row} />
              ))}
            </section>
          );
        })()}
        
        {/* ✓ FINISHED - Resolved allocations (FINAL status) */}
        {(() => {
          const finishedRows = sortedRows.filter(r => r.status === 'FINAL');
          return finishedRows.length > 0 && (
            <section className="mb-4">
              <div className="flex items-center gap-2 mb-2 px-4">
                <span className="text-base">✓</span>
                <h2 className="text-[12px] font-black text-slate-500 uppercase tracking-wider">
                  Finished ({finishedRows.length})
                </h2>
              </div>
              {finishedRows.map((row, index) => (
                <AllocationRow key={`finished-${row.slot}-${index}`} row={row} />
              ))}
            </section>
          );
        })()}
        
      </main>

      {/* ===================================================================== */}
      {/* STICKY FOOTER HUD - Game Plan Summary (V9) - Apple HIG Compliant */}
      {/* ===================================================================== */}
      <div className="fixed bottom-0 left-0 right-0 bg-slate-900 backdrop-blur-md border-t border-slate-700 safe-area-pb">
        {/* Summary Stats */}
        <div className="px-4 py-4 border-b border-slate-800">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg">🎯</span>
            <span className="text-[13px] font-black text-white uppercase tracking-wider">
              Game Plan Summary
            </span>
          </div>
          
          {/* Row 1: Resolved + HIT/MISS */}
          <div className="flex items-center justify-between text-[14px] mb-2">
            <span className="text-slate-300">
              {resolvedCount} of {rows.length} resolved
            </span>
            <span>
              <span className="text-emerald-400 font-bold">{hitsCount} HIT</span>
              <span className="text-slate-500 mx-1">•</span>
              <span className="text-red-400 font-bold">{missesCount} MISS</span>
            </span>
          </div>
          
          {/* Row 2: On Field / In Locker + Earned */}
          <div className="flex items-center justify-between text-[14px] mb-2">
            <span className="text-slate-300">
              <span className="text-emerald-400 font-bold">{onFieldCount}</span> on field
              <span className="text-slate-500 mx-1">•</span>
              <span className="text-white font-bold">{inLockerCount}</span> in locker
            </span>
            <span className={`text-[16px] font-mono font-black ${earnedPoints >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {earnedPoints >= 0 ? '+' : ''}{earnedPoints} earned
            </span>
          </div>
          
          {/* Row 3: Cap breakdown */}
          <div className="flex items-center justify-between text-[13px] text-slate-400">
            <span>
              {impactOnField} cap on field • {impactInLocker} cap in locker
            </span>
          </div>
        </div>
        
        {/* Back Button */}
        <div className="px-4 py-3">
          <button 
            onClick={handleBack}
            className="w-full bg-slate-800 hover:bg-slate-700 text-white font-bold text-[14px] py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2 min-h-[48px] active:scale-[0.98]"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back to Scoreboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default FieldReportPage;
